import React from 'react';
import { Link } from 'react-router-dom';
import { Car, ArrowLeft, FileText, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const Terms = () => {
  const sections = [
    {
      title: "1. The Basics",
      content: `By using Reddi (the app, website, or any of our services), you're agreeing to these terms. If you don't agree, that's cool—but you can't use our service.

These terms apply to everyone: riders, drivers, and anyone just browsing. We might update these from time to time, and if you keep using Reddi after changes, that means you're good with them.`
    },
    {
      title: "2. What Reddi Is (and Isn't)",
      content: `Reddi is a technology platform that connects people who need rides with independent drivers who can provide them. Important distinction: we're not a transportation company. We don't own cars or employ drivers directly—we just make the connection happen.

The service includes:
• Apps for riders and drivers
• Fare calculation and payment processing  
• GPS-based location services
• Communication tools between riders and drivers
• Rating and review systems`
    },
    {
      title: "3. Your Account",
      content: `To use Reddi, you'll need to create an account. When you do, you agree to:
• Give us accurate information (no fake names, please)
• Keep your password secure
• Tell us immediately if someone hacks your account
• Take responsibility for everything that happens under your login

You need to be 18+ to have an account. Drivers have additional requirements—check our Driver Agreement for those.`
    },
    {
      title: "4. Rules for Riders",
      content: `As a rider, here's what we expect:
• Be respectful to your driver (they're people too)
• Give accurate pickup and destination info
• Be ready when your driver arrives (waiting = money)
• Wear your seatbelt
• Don't do anything illegal during rides
• Pay for your rides (duh)
• Rate drivers honestly

We can suspend or terminate accounts that break these rules. Don't be that person.`
    },
    {
      title: "5. How Payments Work",
      content: `Fares are based on distance, time, and how busy things are when you request. You're agreeing to pay:
• Base fare plus per-mile and per-minute charges
• Service fees (we show you before you book)
• Surge pricing during peak times
• Tolls and taxes
• Tips if you want to (drivers keep 100% of tips)

We charge your payment method automatically after the ride. If you think something's wrong, let us know within 7 days.`
    },
    {
      title: "6. Cancellations",
      content: `Life happens, we get it. Here's the deal with canceling:
• Within 2 minutes of requesting: free
• Before a driver is assigned: free
• After a driver's on their way: might be a fee
• Scheduled rides: cancel up to 1 hour before, no charge

If you're constantly canceling on drivers, we might need to have a conversation.`
    },
    {
      title: "7. Safety Stuff",
      content: `We work hard to make Reddi safe, but here's the reality:
• Drivers are independent contractors, not our employees
• Background checks have limits—they might not catch everything
• You're using the service at your own risk

We encourage everyone to verify driver and vehicle info before getting in, share trip details with friends, and report any concerns immediately. Safety is a team effort.`
    },
    {
      title: "8. Our Stuff (Intellectual Property)",
      content: `The Reddi name, logo, and all our branding—those are ours. You can't use them without asking.

Everything in the app (text, graphics, code, etc.) is protected by copyright. Please don't copy it.`
    },
    {
      title: "9. Privacy",
      content: `Your privacy matters to us. Check out our Privacy Policy for the full details on how we handle your data. By using Reddi, you're saying you're okay with those practices.`
    },
    {
      title: "10. Liability Limits",
      content: `THE LEGAL STUFF (READ THIS):

We're not responsible for indirect damages, lost profits, or things that happen because of:
• Using (or not being able to use) Reddi
• Other people's behavior on the platform
• Security issues

Our total liability is capped at $100 or what you've paid us in the past year—whichever is more.

We know this sounds harsh, but it's standard for tech platforms and lets us keep prices fair for everyone.`
    },
    {
      title: "11. Disputes",
      content: `If we have a disagreement, we'll handle it through arbitration (basically a private resolution process) instead of going to court. You're also agreeing not to join class action lawsuits against us.

For smaller issues, you can go to small claims court if you prefer.`
    },
    {
      title: "12. Account Termination",
      content: `We can end your account at any time for breaking these terms. If that happens:
• You lose access to Reddi immediately
• We might delete your data
• Some parts of these terms still apply even after you're gone`
    },
    {
      title: "13. Questions?",
      content: `Hit us up:

Reddi, Inc.
hello@reddi.com

We're real people who actually read our emails.`
    }
  ];

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="py-6 px-6 md:px-12 border-b border-slate-800">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-red-600 flex items-center justify-center transform -skew-x-6">
              <Car className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-['Barlow_Condensed'] font-black text-white">REDDI</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-slate-400 hover:text-white gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <FileText className="w-12 h-12 text-red-500 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-['Barlow_Condensed'] font-black text-white uppercase mb-4">
            Terms of Service
          </h1>
          <p className="text-slate-400">
            Last updated: January 2025
          </p>
        </div>
      </section>

      {/* Intro Note */}
      <section className="py-8 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-4 p-6 bg-amber-600/10 border border-amber-600/30 mb-12">
            <AlertCircle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-300 font-medium mb-2">Quick heads up</p>
              <p className="text-slate-400">
                We've tried to make these terms readable (lawyers hate us), but they're still a legal document. 
                The casual language doesn't change what you're agreeing to.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-8 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-invert max-w-none">
            {sections.map((section, index) => (
              <div key={index} className="mb-12">
                <h2 className="text-xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-4">
                  {section.title}
                </h2>
                <div className="text-slate-400 whitespace-pre-line leading-relaxed">
                  {section.content}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-800">
        <div className="max-w-7xl mx-auto text-center text-slate-500 text-sm">
          © 2025 REDDI. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default Terms;
